import {
  positionBetween,
  type AttachmentDto,
  type BulkNotesInput,
  type BulkResultDto,
  type CreateNoteInput,
  type NoteDto,
  type UpdateNoteInput,
  type UserDto,
} from '@keep/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { meKey } from './auth';
import {
  applyNote,
  findCachedNote,
  noteKeys,
  removeNote,
  restoreSnapshot,
  snapshotNotes,
  type NoteFilter,
} from './notes-cache';

export { noteKeys, type NoteFilter } from './notes-cache';

function toQueryString(filter: NoteFilter): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filter)) {
    if (value !== undefined && value !== '') params.set(key, String(value));
  }
  return params.toString();
}

export function useNotes(filter: NoteFilter) {
  return useQuery({
    queryKey: noteKeys.list(filter),
    queryFn: () => api.get<NoteDto[]>(`/notes?${toQueryString(filter)}`),
    // Pendant une recherche, on garde les anciens résultats affichés le temps de charger les nouveaux.
    placeholderData: filter.q ? keepPreviousData : undefined,
  });
}

export function useNote(id: string | null) {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: noteKeys.detail(id ?? ''),
    queryFn: () => api.get<NoteDto>(`/notes/${id}`),
    enabled: Boolean(id),
    initialData: () => (id ? findCachedNote(queryClient, id) : undefined),
    initialDataUpdatedAt: 0,
  });
}

export function useCreateNote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateNoteInput) => api.post<NoteDto>('/notes', input),
    onSuccess: (note) => applyNote(queryClient, note),
  });
}

/** Applique localement un patch sur une note, avec les mêmes règles que l'API. */
export function applyPatch(note: NoteDto, patch: UpdateNoteInput): NoteDto {
  const next: NoteDto = {
    ...note,
    ...(patch.title !== undefined && { title: patch.title }),
    ...(patch.content !== undefined && { content: patch.content }),
    ...(patch.type !== undefined && { type: patch.type }),
    ...(patch.color !== undefined && { color: patch.color }),
    ...(patch.labelIds !== undefined && { labelIds: patch.labelIds }),
    ...(patch.reminderAt !== undefined && { reminderAt: patch.reminderAt }),
    ...(patch.items !== undefined && {
      items: patch.items.map((item) => ({
        id: item.id ?? crypto.randomUUID(),
        text: item.text,
        checked: item.checked ?? false,
      })),
    }),
  };
  if (patch.archived !== undefined) {
    next.archived = patch.archived;
    if (patch.archived) next.pinned = false;
  }
  if (patch.pinned !== undefined) {
    next.pinned = patch.pinned;
    if (patch.pinned) next.archived = false;
  }
  return next;
}

/** Sauvegarde d'une note (contenu ou état), avec affichage optimiste. */
export function useUpdateNote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['note-update'],
    mutationFn: ({ id, patch }: { id: string; patch: UpdateNoteInput }) =>
      api.patch<NoteDto>(`/notes/${id}`, patch),
    onMutate: async ({ id, patch }) => {
      await queryClient.cancelQueries({ queryKey: noteKeys.all });
      const snapshot = snapshotNotes(queryClient);
      const current = findCachedNote(queryClient, id);
      if (current) applyNote(queryClient, applyPatch(current, patch));
      return { snapshot };
    },
    onError: (_error, _variables, context) => {
      if (context) restoreSnapshot(queryClient, context.snapshot);
    },
    onSuccess: (note) => {
      // Si d'autres sauvegardes de cette note sont en vol, cette réponse est déjà périmée : on attend la dernière.
      const pending = queryClient.isMutating({
        mutationKey: ['note-update'],
        predicate: (mutation) =>
          (mutation.state.variables as { id?: string } | undefined)?.id === note.id,
      });
      if (pending <= 1) applyNote(queryClient, note);
    },
  });
}

/** Effet local d'une action groupée, pour l'affichage optimiste. */
function applyBulk(
  note: NoteDto,
  input: BulkNotesInput,
  me: UserDto | null | undefined,
): NoteDto | null {
  switch (input.action) {
    case 'pin':
      return applyPatch(note, { pinned: true });
    case 'unpin':
      return applyPatch(note, { pinned: false });
    case 'archive':
      return applyPatch(note, { archived: true });
    case 'unarchive':
      return applyPatch(note, { archived: false });
    case 'color':
      return { ...note, color: input.color };
    case 'addLabel':
      return note.labelIds.includes(input.labelId)
        ? note
        : { ...note, labelIds: [...note.labelIds, input.labelId] };
    case 'removeLabel':
      return { ...note, labelIds: note.labelIds.filter((id) => id !== input.labelId) };
    case 'trash':
      // Un collaborateur qui « supprime » quitte la note : elle disparaît simplement.
      return me && note.owner.id !== me.id
        ? null
        : { ...note, pinned: false, trashedAt: new Date().toISOString() };
    case 'restore':
      return { ...note, trashedAt: null };
    case 'delete':
      return null;
  }
}

/** Toutes les actions de la grille (épingler, archiver, couleur, corbeille…) passent par ici. */
export function useBulkNotes() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: BulkNotesInput) => api.post<BulkResultDto>('/notes/bulk', input),
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: noteKeys.all });
      const snapshot = snapshotNotes(queryClient);
      const me = queryClient.getQueryData<UserDto | null>(meKey);
      for (const id of input.ids) {
        const current = findCachedNote(queryClient, id);
        if (!current) continue;
        const next = applyBulk(current, input, me);
        if (next) applyNote(queryClient, next);
        else removeNote(queryClient, id);
      }
      return { snapshot };
    },
    onError: (_error, _input, context) => {
      if (context) restoreSnapshot(queryClient, context.snapshot);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: noteKeys.lists }),
  });
}

export function useMoveNote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      beforeId,
      afterId,
    }: {
      id: string;
      beforeId: string | null;
      afterId: string | null;
    }) => api.post<NoteDto>(`/notes/${id}/move`, { beforeId, afterId }),
    // Synchrone (pas d'await) : la grille doit refléter le nouvel ordre dès le dépôt.
    onMutate: ({ id, beforeId, afterId }) => {
      void queryClient.cancelQueries({ queryKey: noteKeys.lists });
      const note = findCachedNote(queryClient, id);
      const above = beforeId ? findCachedNote(queryClient, beforeId)?.position : undefined;
      const below = afterId ? findCachedNote(queryClient, afterId)?.position : undefined;
      if (!note || (beforeId && above === undefined) || (afterId && below === undefined)) return;
      const position = positionBetween(above ?? null, below ?? null);
      if (position !== null) applyNote(queryClient, { ...note, position });
    },
    onSuccess: (note) => applyNote(queryClient, note),
    // Le serveur a pu renuméroter les positions : on resynchronise les listes.
    onSettled: () => queryClient.invalidateQueries({ queryKey: noteKeys.lists }),
  });
}

export function useCopyNote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<NoteDto>(`/notes/${id}/copy`),
    onSuccess: (note) => applyNote(queryClient, note),
  });
}

export function useEmptyTrash() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete<{ deleted: number }>('/notes/trash'),
    onSettled: () => queryClient.invalidateQueries({ queryKey: noteKeys.all }),
  });
}

export function useAddCollaborator(noteId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (email: string) => api.post<NoteDto>(`/notes/${noteId}/collaborators`, { email }),
    onSuccess: (note) => applyNote(queryClient, note),
  });
}

export function useRemoveCollaborator(noteId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => api.delete(`/notes/${noteId}/collaborators/${userId}`),
    onSuccess: (_result, userId) => {
      const me = queryClient.getQueryData<UserDto | null>(meKey);
      if (me?.id === userId) {
        removeNote(queryClient, noteId);
        return;
      }
      const note = findCachedNote(queryClient, noteId);
      if (note) {
        applyNote(queryClient, {
          ...note,
          collaborators: note.collaborators.filter((c) => c.id !== userId),
        });
      }
    },
  });
}

export function useUploadAttachment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ noteId, file }: { noteId: string; file: File }) => {
      const form = new FormData();
      form.append('file', file);
      return api.post<AttachmentDto>(`/notes/${noteId}/attachments`, form);
    },
    onSuccess: (attachment, { noteId }) => {
      const note = findCachedNote(queryClient, noteId);
      if (note) applyNote(queryClient, { ...note, attachments: [...note.attachments, attachment] });
    },
  });
}

export function useDeleteAttachment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ attachmentId }: { noteId: string; attachmentId: string }) =>
      api.delete(`/attachments/${attachmentId}`),
    onMutate: ({ noteId, attachmentId }) => {
      const snapshot = snapshotNotes(queryClient);
      const note = findCachedNote(queryClient, noteId);
      if (note) {
        applyNote(queryClient, {
          ...note,
          attachments: note.attachments.filter((a) => a.id !== attachmentId),
        });
      }
      return { snapshot };
    },
    onError: (_error, _variables, context) => {
      if (context) restoreSnapshot(queryClient, context.snapshot);
    },
  });
}
